import { format } from 'svelte-i18n';
//Stores
import { get } from 'svelte/store';
import { navigateToDevice, navigateToSetting } from 'src/stores/ui/navigate-to';
import { user_settings } from 'src/stores/account/user';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { ai_model_ready } from 'src/stores/features/ai-model';
import { devices } from 'src/stores/lists/devices';
import {
	dismissed_notifications,
	additional_notifications,
	device_notifications,
} from 'src/stores/features/device-notifications';
//Helpers
import { capFirstChar } from '../utilities';
import {
	is_gen_2,
	batLevelGenOneExtPwrDev,
	isWallDisplay,
	is_loqed,
	isBluTRV,
	is_GBLE,
	get_gen_2_component_channel_structure,
	isNonRechargeableBattery,
	isDiffuser,
	real_id,
	isCameraDevice,
} from '../interface_helpers';
import { handleDeviceAssoc } from '../associate_cloud';
import { minisStatisticsForceUpdateCheck } from '../firmware_speciffic_conditions';
import { checkGatewayReportOlderThanDay, checkGatewaysBluetooth } from '../device_data_helpers';
import {
	isBluTRVCalibrated,
	isBluTRVExtTempMissing,
	isBluTRVMounted,
} from '../virtual_helpers/blutrv_helpers';
import { copy } from 'src/helpers/copy_text';
import { navigateToShop } from 'src/components/Lists/list-helpers';
import { getLoraNotificationTargetDevice } from '../addons/addon_lora_helpers';
import { isVirtualDevice } from '../virtual_helpers/virtual_components_helpers';
//Types
import type { Device } from 'src/types/Device';
import { DeviceNotificationBuilder } from 'src/helpers/notifications/DeviceNotificationBuilder';
import { DeviceNotification } from './DeviceNotification';
import { DIMMERS_WITH_CALIBRATION } from 'src/constants';
import { showNewLayout } from 'src/stores/toDelete/new_layout';
import {
	formatEmptyVialMessage,
	getSlotVialName,
	parseVialFaultNotification,
} from '../cury_vial_helpers';
import {
	buildCameraDeviceNotification,
	collectCameraDeviceNotificationConditions,
} from 'src/components/Camera/helpers/camera-notification-helpers';

/**
 * This helper contains most of the logic for the Device Notifications module.
 * All notifications condition are defined in devicesNotificationsConditions() function.
 *
 * All notifications are built in buildNotification() function. It uses the @DeviceNotificationBuilder class to build the notification object,
 * which relies on the "builder" pattern.
 *
 * Adding new notification requires these steps:
 * 1. Add a new condition in devicesNotificationsConditions() function;
 * 2. Add a new case in buildNotification() function;
 * 3. Add a new priority in device_notifications_priorities.ts file;
 * The notification name should be unique and should be used in both functions.
 *
 * Some of the notifications are obtained directly from the device status (as we call them structured errors), and they don't require
 * a condition to be checked.
 *
 */

/**
 * TypeScript type definitions for device notifications
 */

/**
 * Vial fault types supported by the system
 */
type VialFaultType = 'non_genuine' | 'tag_error' | 'expired' | 'empty';

/**
 * Call-to-Action button object structure
 */
interface NotificationCta {
	title: string;
	onClick: () => void | string;
}

/**
 * Vial fault error information structure
 */
interface VialFaultErrorInfo {
	short: string;
	error: string;
}

export const _format = get(format);

let interval: ReturnType<typeof setTimeout> | null = null;
const NOTIFICATION_INTERVAL_MS = 1000; //ms; Process notifications interval

/**
 * Helper functions for creating common CTA patterns in notifications
 * These reduce code duplication and ensure consistency
 */

/**
 * Creates a "Go to device" CTA button
 * @param deviceId - The ID of the device to navigate to
 * @returns A CTA object for navigating to the device
 */
const createGoToDeviceCta = (deviceId: string): NotificationCta => ({
	title: capFirstChar(_format('navigate_to_device', { default: 'Go to device' })),
	onClick: () => navigateToDevice(deviceId),
});

/**
 * Creates a "Go to Safety Tab" CTA button with optional sub-setting
 * @param deviceId - The ID of the device
 * @param subSetting - Optional specific safety setting to navigate to
 * @returns A CTA object for navigating to the safety tab
 */
const createGoToSafetyCta = (deviceId: string, subSetting?: string): NotificationCta => ({
	title: capFirstChar(_format('go_to_safety_btn_title', { default: 'Go to Safety Tab' })),
	onClick: () => navigateToSetting(deviceId, 'safety', subSetting),
});

/**
 * Creates a general settings navigation CTA button
 * @param deviceId - The ID of the device
 * @param settingsTab - The settings tab to navigate to
 * @param subSetting - Optional specific sub-setting within the tab
 * @param customTitle - Optional custom title for the CTA button
 * @returns A CTA object for navigating to the specified setting
 */
const createGoToSettingCta = (
	deviceId: string,
	settingsTab: string,
	subSetting?: string,
	customTitle?: string,
): NotificationCta => ({
	title: customTitle || capFirstChar(_format('settings', { default: 'Settings' })),
	onClick: () => navigateToSetting(deviceId, settingsTab, subSetting),
});

/**
 * Process notifications on background every second.
 */
export const startNotificationsProcessing = () => {
	if (interval) {
		clearTimeout(interval);
		interval = null;
	}

	interval = setTimeout(() => {
		// Start the notifications processing interval
		// console.log('[Timer] Start processing device_notifications...', Date().toLocaleString());
		// let started = Date.now();
		processNotifications();
		// console.log("[NOTIFICATIONS] Processed device_notifications (ms%d)...", Date.now()-started, Date().toLocaleString());

		// Schedule the next evaluation
		startNotificationsProcessing();
	}, NOTIFICATION_INTERVAL_MS);
};

/**
 * Stop the notifications processing
 */
export const stopNotificationsProcessing = () => {
	// console.warn("[Timer] STOP: device_notifications timer", Date().toLocaleString());
	if (interval) {
		clearTimeout(interval);
		interval = null;
	}
};

/**
 * Stop the notifications processing and clear the device notifications store
 */
export const stopAndClearNotifications = () => {
	stopNotificationsProcessing();
	device_notifications.set({});
};

/**
 * Function to filter the critical notifications for a provided notifications list.
 * Used to add indication in device cards, devcie drawer main controls and other UI points.
 * @param notifications - List with notifications to be filtered
 * @returns - Array with translated keys and notification' type for the critical notifications
 */
export const getCriticalNotifications = (notifications: { [category: string]: DeviceNotification }) => {
	return (
		Object.values(notifications)
			.filter((notification) => notification?.error)
			.sort((a, b) => (a.priority ?? 1000) - (b.priority ?? 1000))
			.map((notification) => ({
				name: _format(
					'text_device_' + notification.error.toLowerCase() + '_notification_option',
					{
						default: notification.error.replace(/_/g, ' '),
					},
				),
				type: notification.type,
			})) || []
	);
};

/**
 * Get the active notifications for a single device.
 * @param deviceId - Device ID to filter notifications for
 * @returns object with notifications for the given device ID
 */
export const getNotificationsForDevice = (
	deviceId: string,
): { [category: string]: DeviceNotification } => {
	if (!deviceId || !get(device_notifications)) {
		return {};
	}

	return (
		Object.fromEntries(
			[...Object.entries(get(device_notifications))]
				.filter(([notificationId]) => {
					const extractedId = notificationId?.split('-')[0];
					const targetId = getLoraNotificationTargetDevice(deviceId, notificationId);
					if (targetId === null) return false;
					const isDeviceKnown = extractedId in get(devices);
					const isForCurrentDevice = targetId === extractedId;
					const isNotDismissed = !(notificationId in get(dismissed_notifications));

					return isDeviceKnown && isForCurrentDevice && isNotDismissed;
				})
				// Sort by priority
				.sort(([, a], [, b]) => (a.priority ?? 1000) - (b.priority ?? 1000)),
		) || {}
	);
};

/**
 * Function to process notifications and update the store
 *   IMPORTANT: This function is called every second,
 * 		 Keep it as fast as possible.
 */
let processNotifications = () => {
	const status = get(devices_status);
	const settings = get(devices_settings);
	const allDevices = get(devices);

	let notificationsList = {};

	for (const deviceId in allDevices) {
		const device = allDevices?.[deviceId];
		const deviceStatus = status?.[real_id(deviceId)];
		const deviceSettings = settings?.[real_id(deviceId)];

		// Preventing notifications for shared devices, missing devices and virtual devices
		if (
			!device ||
			!deviceStatus ||
			!deviceSettings ||
			device.shared ||
			isVirtualDevice(device.type)
		) {
			continue;
		}

		// Evaluate notifications conditions
		let notifications = devicesNotificationsConditions(device, deviceStatus, deviceSettings);

		// For every present notification, build its object
		for (const notif in notifications) {
			if (notifications[notif]) {
				notificationsList[`${deviceId}-${notif}`] = {
					...notificationsList[`${deviceId}-${notif}`],
					...buildNotification(notif, device, deviceStatus, deviceSettings),
				};
			} else {
				delete notificationsList[`${deviceId}-${notif}`];
			}
		}
	}

	// Update the store with the current list
	device_notifications.set(notificationsList);
};

/**
 * Function to examinate the conditions for device notifications
 * @param device - The device object
 * @param status - The device status object
 * @param settings - The device settings object
 * @returns Collection with all notifications in format `[notif: string]: boolean`,
 * where the boolean flag is if the notification is present
 */
export const devicesNotificationsConditions = (device: Device, status, settings) => {
	const notifications: { [notif: string]: boolean } = {};

	const rssi =
		typeof status?.wifi_sta?.rssi !== 'undefined' ? status?.wifi_sta?.rssi : status?.wifi?.rssi;
	const batLevel =
		is_gen_2(device) && !status?.['devicepower:0']?.external?.present
			? status?.['devicepower:0']?.battery?.percent
			: batLevelGenOneExtPwrDev(device.type, status, settings);
	const aiModelReadyDevices = get(ai_model_ready);

	const bluLowBatLevel = device.gen == 'GBLE' && status?.['devicepower:0']?.battery?.low;

	// Get the channel object structure for the device
	const channelStructure = get_gen_2_component_channel_structure(device);

	const structuredErrors: string[] =
		(status?.online &&
			(status?.[channelStructure]?.errors ||
				status?.[channelStructure]?.a_errors ||
				status?.[channelStructure]?.b_errors ||
				status?.[channelStructure]?.c_errors ||
				status?.[channelStructure]?.n_errors ||
				status?.['service:0']?.errors ||
				status?.['service:0']?.flags)) ||
		[];

	notifications['weakSignal'] ||= rssi < -80;

	notifications['overTemperature'] ||= status?.overtemperature;

	notifications['overpower'] ||= checkOverpowerCondition(status, settings, device?.channel);

	notifications['humidityError'] ||=
		status?.sensor_error > 0 &&
		status?.hum &&
		(status.hum?.value == null || status.hum?.value === 999);

	notifications['temperatureError'] ||=
		status?.sensor_error > 0 &&
		status?.tmp &&
		(status.tmp?.value == null || status.tmp?.value === 999);

	notifications['highTemperature'] ||= status?.temperature > 84;

	notifications['dali_control_gear_missing'] ||= status?.dali?.errors === 'dali_control_gear_missing';

	notifications['dali_gear'] ||= status?.dali?.errors === 'dali_gear';

	notifications['rebootRequired'] ||= status.online && status?.sys?.restart_required;

	notifications['bleDisabled'] ||= is_GBLE(device) && checkGatewaysBluetooth(device);

	notifications['gatewayReportOlderThanDay'] ||=
		is_GBLE(device) && device.category !== 'inputs_reader' && checkGatewayReportOlderThanDay(device);

	// notifications['promoAlert'] ||=
	// 	status.online &&
	// 	isWallDisplay(device.type) &&
	// 	device.category === 'relay' &&
	// 	typeof get(user_settings)?.ht_codes?.[device.id] !== 'undefined' &&
	// 	status?.sys?.batch_date < 240226;

	// notifications['promoBlu'] ||=
	// 	status?.online &&
	// 	isWallDisplay(device.type) &&
	// 	device.category === 'relay' &&
	// 	typeof get(user_settings)?.ht_codes?.[device.id] === 'undefined' &&
	// 	status?.sys?.batch_date < 240226 &&
	// 	typeof get(user_settings)?.ht_codes?.maniac !== 'undefined' &&
	// 	get(user_settings)?.ht_codes?.maniac;

	// notifications['promoBluComing'] ||=
	// 	status?.online &&
	// 	isWallDisplay(device.type) &&
	// 	device.category === 'relay' &&
	// 	typeof get(user_settings)?.ht_codes?.[device.id] === 'undefined' &&
	// 	status?.sys?.batch_date < 240226;

	notifications['ai_is_completed'] ||=
		aiModelReadyDevices.hasOwnProperty(device.id) &&
		aiModelReadyDevices[device.id].hasOwnProperty('model_ready') &&
		aiModelReadyDevices[device.id].model_ready;

	notifications[`${aiModelReadyDevices[device.id]?.fail_detection}`] ||=
		aiModelReadyDevices.hasOwnProperty(device.id) &&
		aiModelReadyDevices[device.id].hasOwnProperty('fail_detection') &&
		aiModelReadyDevices[device.id].fail_detection.length > 0;

	notifications['lowBattery'] ||= batLevel && batLevel < 20 && batLevel > 10;

	notifications['bluLowBattery'] ||= bluLowBatLevel;

	notifications['criticalBattery'] ||= batLevel && batLevel < 10;

	notifications['batteryError'] ||=
		batLevel === null ||
		batLevel === 'null' ||
		(status?.bat &&
			(status.bat?.value == null || status.bat?.value === 'null' || status.bat?.value === 999));

	notifications['cableUnplugged'] ||= status?.['flood:0']?.errors?.includes('cable_unplugged');

	notifications['update_required'] ||=
		(is_gen_2(device) && settings && minisStatisticsForceUpdateCheck(device, settings)) ||
		(status?.online && typeof status?.sys?.batch_date === 'undefined' && isWallDisplay(device.type));

	notifications['loqedWarning'] ||= is_loqed(device) && status?.bolt_state === 'unknown';

	notifications['mount_error'] ||= status?.online && isBluTRV(device.type) && !isBluTRVMounted(device);

	notifications['ext_temp_missing'] ||=
		status?.online && isBluTRV(device.type) && isBluTRVExtTempMissing(device);

	notifications['calibration_error'] ||=
		status?.online &&
		isBluTRV(device.type) &&
		!isBluTRVCalibrated(device) &&
		!notifications?.['mount_error'];

	notifications['lora_duty_cycle_limit'] ||=
		status?.['lora:100']?.flags && status?.['lora:100']?.flags.includes('duty_cycle_limit');

	notifications['lora_limit_reached'] ||=
		status?.['lora:100'] &&
		status?.['lora:100']?.errors &&
		status?.['lora:100'].errors.includes('limit_reached');

	notifications['lora_send_blocked'] ||=
		status?.['lora:100'] &&
		status?.['lora:100']?.flags &&
		status?.['lora:100'].flags.includes('send_blocked');

	if (notifications['lora_limit_reached'] && notifications['lora_duty_cycle_limit']) {
		delete notifications['lora_duty_cycle_limit'];
	}

	notifications['data_error'] ||= status?.['emdata:0']?.errors;

	if (isCameraDevice(device)) {
		Object.assign(notifications, collectCameraDeviceNotificationConditions(device, status));
	}

	// Check for vial faults (for Cury devices)
	if (isDiffuser(device.type)) {
		const vialFaults = getVialFaultError(status?.[channelStructure]?.slots);
		Object.keys(vialFaults).forEach((slot) => {
			const faultType = vialFaults[slot];
			notifications[`vial_fault_${slot}_${faultType}`] = true;
		});
	}

	// Filter structured errors
	// Obtained from the device' status itself
	if (structuredErrors?.length > 0) {
		structuredErrors.forEach((err) => {
			notifications[err] = true;
		});
	}

	// Filter additional notifications
	// Saved in 'additional_notifications' store, obtained from different cases
	if (get(additional_notifications)?.[device.id]?.length > 0) {
		get(additional_notifications)?.[device.id]?.forEach((notif) => {
			notifications[notif] = true;
		});
	}

	return notifications;
};

/**
 * Function to build the notification object for a given notification
 * @param notification - The notification type name (can be any string as notifications are dynamically generated)
 * @param device - The device object
 * @param status - The device status object
 * @param settings - The device settings object
 * @returns A DeviceNotification object or undefined if parameters are invalid
 */
export const buildNotification = (notification: string, device: Device, status, settings) => {
	if (
		typeof device === 'undefined' ||
		typeof status === 'undefined' ||
		typeof settings === 'undefined'
	) {
		return;
	}

	const batLevel =
		(is_gen_2(device) && !status?.['devicepower:0']?.external?.present) ||
		(device.gen == 'GBLE' && status?.['devicepower:0']?.battery?.low)
			? status?.['devicepower:0']?.battery?.percent /**HnT+ or maybe more*/
			: // if device has external power then don't create battery error notification
				// settings?.external_power is for H&T
				// status?.charger is for trv and motion
				batLevelGenOneExtPwrDev(device.type, status, settings);

	const code = get(user_settings)?.ht_codes?.[device.id]?.code;

	if (isCameraDevice(device)) {
		const cameraNotification = buildCameraDeviceNotification(notification, device);
		if (cameraNotification) {
			return cameraNotification;
		}
	}

	switch (notification) {
		case 'mount_error':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format('notification_global_mount_error', {
						default: '{deviceName} is not mounted',
						values: { deviceName: device.name },
					}),
					local: _format('notification_global_mount_error', {
						default: '{deviceName} is not mounted',
						values: { deviceName: device.name },
					}),
				})
				.setMessage(
					_format('message_notification_mount_error', {
						default:
							'To use the full power of the device, it needs to be mounted and calibrated.',
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('mount_error')
				.build();
		case 'associateCloud':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format('notification_global_cloud_associate', {
						default: `${device?.name} is not associated with your account.`,
						values: { deviceName: device?.name },
					}),
					local: _format('notification_device_drawer_cloud_associate', {
						default: `Device is not associated with your account.`,
					}),
				})
				.setCtaGlobal({
					title: capFirstChar(
						_format('button_associate_cloud', {
							default: 'associate',
						}),
					),
					onClick: () => handleDeviceAssoc(device.id),
				})
				.setCtaLocal({
					title: capFirstChar(
						_format('button_associate_cloud', {
							default: 'associate',
						}),
					),
					onClick: () => handleDeviceAssoc(device.id),
				})
				.build();
		case 'overTemperature':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${capFirstChar(
						_format(`overtemp_detected`, {
							values: { type: 'overtemp' },
							default: `overtemp detected`,
						}),
					)}`,
					local: _format('notification_device_drawer_over_temperature', {
						default: `Device switched off due to high temperature.`,
					}),
				})
				.setMessage(
					_format('error_over_temp_alert', {
						default:
							'Exceeded the configured temperature limit. The device has been turned off to prevent damage.`',
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('overtemp')
				.build();

		case 'overpower':
			const overpowerCta = createGoToSafetyCta(device.id, 'max_power_protection');
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${capFirstChar(
						_format('overpower_detected', { default: 'Overpower detected' }),
					)}`,
					local: capFirstChar(
						_format('overpower_detected', { default: 'Overpower detected' }),
					),
				})
				.setMessage(
					_format('error_over_power_alert', {
						default: `Exceeded the configured power limit. The device has been turned off to prevent damage.`,
					}),
				)
				.setError('overpower')
				.setCtaGlobal(overpowerCta)
				.setCtaLocal(overpowerCta)
				.build();

		case 'weakSignal':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'info')
				.setHeading({
					global: _format('notification_global_weak_wifi', {
						values: { deviceName: device.name },
						default: `${device.name} has weak Wi-Fi signal. We suggest, if possible, moving the device closer to the router or extending your Wi-Fi network signal.`,
					}),
					local: _format('notification_device_drawer_weak_wifi', {
						default: `Weak Wi-Fi signal. We suggest, if possible, moving the device closer to the router or extending your Wi-Fi network signal.`,
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'humidityError':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format('notification_global_humidity_error', {
						default: `${device.name} has humidity sensor error detected.`,
						values: { deviceName: device.name },
					}),
					local: _format('notification_device_drawer_humidity_error', {
						default: `Humidity sensor error detected.`,
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'temperatureError':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format('notification_global_temperature_error', {
						default: `${device.name} has temperature sensor error detected.`,
						values: { deviceName: device.name },
					}),
					local: _format('notification_device_drawer_temperature_error', {
						default: `Temperature sensor error detected.`,
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'highTemperature':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format('notification_global_high_temperature', {
						default: `${device.name} is running hot: ${status?.temperature}°`,
						values: {
							deviceName: device.name,
							temperature: status?.temperature,
						},
					}),
					local: _format('notification_device_drawer_high_temperature', {
						default: `Device is running hot: ${status?.temperature}°`,
						values: { temperature: status?.temperature },
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'dali_control_gear_missing':
			const daliConfigCta = createGoToSettingCta(device.id, 'settings', 'dali_configuration');
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format('error_control_gear_disconnected', {
						default: `${device.name} disconnected control gear.`,
						values: { deviceName: device.name },
					}),
					local: _format('error_control_gear_disconnected', {
						default: `${device.name} disconnected control gear.`,
						values: { deviceName: device.name },
					}),
				})
				.setCtaGlobal(daliConfigCta)
				.setCtaLocal(daliConfigCta)
				.build();

		case 'dali_gear':
			const daliGearCta = createGoToSettingCta(device.id, 'settings', 'dali_configuration');
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format(`error_gear_is_missing`, {
						default: `${device.name} gear is missing`,
						values: { deviceName: device.name },
					}),
					local: _format(`error_gear_is_missing`, {
						default: `${device.name} gear is missing`,
						values: { deviceName: device.name },
					}),
				})
				.setCtaGlobal(daliGearCta)
				.setCtaLocal(daliGearCta)
				.build();

		case 'rebootRequired':
			const rebootCta = createGoToSettingCta(
				device.id,
				'settings',
				'device_reboot',
				capFirstChar(_format('button_reboot', { default: 'Reboot device' })),
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'info')
				.setHeading({
					global: _format('notification_global_reboot_required', {
						default: `${device.name} requires the device to be rebooted.`,
						values: { deviceName: device.name },
					}),
					local: _format('notification_device_drawer_reboot_required', {
						default: `One or more device changes requires the device to be rebooted in order to take effect. Reboot now?`,
					}),
				})
				.setCtaGlobal(rebootCta)
				.setCtaLocal(rebootCta)
				.build();

		case 'bleDisabled':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('heading_notification_missing_observer', {
						default: 'Missing or corrupted observer',
					}),
					local: _format('heading_notification_missing_observer', {
						default: 'Missing or corrupted observer',
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setCtaLocal({
					title: _format('cta_button_edit_gateway', {
						default: 'Edit Gateway',
					}),
					onClick: () => {
						return 'edit_click';
					},
				})
				.setMessage(
					_format('message_notification_missing_observer', {
						default:
							'Please check the bluetooth device gateways. There may be none assigned or their bluetooth connection turned off.',
					}),
				)
				.setError(`bleDisabled`)
				.build();

		case 'gatewayReportOlderThanDay':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('heading_notification_no_reports', {
						default: 'No recent reports from the gateway',
					}),
					local: _format('heading_notification_no_reports', {
						default: 'No recent reports from the gateway',
					}),
				})
				.setCtaGlobal({
					title: capFirstChar(
						_format(`navigate_to_device`, {
							default: 'go to device',
						}),
					),
					onClick: () => {
						if (get(showNewLayout)) {
							navigateToSetting(device.id, 'bluetooth_gateway');
						} else {
							navigateToDevice(device.id);
						}
					},
				})
				.setCtaLocal({
					title: _format('cta_button_edit_gateway', {
						default: 'Edit Gateway',
					}),
					onClick: () => {
						if (get(showNewLayout)) {
							navigateToSetting(device.id, 'bluetooth_gateway');
						} else {
							return 'edit_click';
						}
					},
				})
				.setMessage(
					_format('message_notification_no_reports', {
						default:
							'The gateway has not reported for over a day. Please check the device itself, its connection to the gateway, and the distance from it.',
					}),
				)
				.setError(`gatewayReportOlderThanDay`)
				.build();

		// case 'promoAlert':
		// 	return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'info')
		// 		.setHeading({
		// 			global: _format('promo_alert_description', {
		// 				default: `Get your free Shelly BLU H&T using this promo code: ${code}`,
		// 				values: { code },
		// 			}),
		// 			local: _format('promo_alert_description', {
		// 				default: `Get your free Shelly BLU H&T using this promo code: ${code}`,
		// 				values: { code },
		// 			}),
		// 		})
		// 		.setCtaGlobal({
		// 			title: _format('button_copy_code', {
		// 				default: `Copy code`,
		// 			}),
		// 			onClick: () => {
		// 				copy(
		// 					code,
		// 					_format('copied_successfully', {
		// 						default: 'Copied successfully!',
		// 					}),
		// 				);
		// 			},
		// 		})
		// 		.setCtaLocal({
		// 			title: _format('button_copy_code', {
		// 				default: `Copy code`,
		// 			}),
		// 			onClick: () => {
		// 				copy(
		// 					code,
		// 					_format('copied_successfully', {
		// 						default: 'Copied successfully!',
		// 					}),
		// 				);
		// 			},
		// 		})
		// 		.build();

		// case 'promoBlu':
		// 	return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'info')
		// 		.setHeading({
		// 			global: _format('promo_alert_blu_heading', {
		// 				default: 'Get your free Shelly BLU H&T!',
		// 			}),
		// 			local: _format('promo_alert_blu_heading', {
		// 				default: 'Get your free Shelly BLU H&T!',
		// 			}),
		// 		})
		// 		.setCtaGlobal({
		// 			title: _format('button_contact_support', {
		// 				default: `Contact support`,
		// 			}),
		// 			onClick: () => {
		// 				navigateToShop('https://www.shelly.com/en-bg/support-knowledge/support');
		// 			},
		// 		})
		// 		.setCtaLocal({
		// 			title: _format('button_contact_support', {
		// 				default: `Contact support`,
		// 			}),
		// 			onClick: () => {
		// 				navigateToShop('https://www.shelly.com/en-bg/support-knowledge/support');
		// 			},
		// 		})
		// 		.build();

		// case 'promoBluComing':
		// 	return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'info')
		// 		.setHeading({
		// 			global: _format('promo_alert_blu_coming_soon', {
		// 				default: 'Your promo code will be accessible in up to 24 hours!',
		// 			}),
		// 			local: _format('promo_alert_blu_coming_soon', {
		// 				default: 'Your promo code will be accessible in up to 24 hours!',
		// 			}),
		// 		})
		// 		.setCtaGlobal({
		// 			title: capFirstChar(
		// 				_format(`navigate_to_device`, {
		// 					default: 'Go to device',
		// 				}),
		// 			),
		// 			onClick: () => {
		// 				navigateToDevice(device.id);
		// 			},
		// 		})
		// 		.build();

		case 'ai_is_completed':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'success')
				.setHeading({
					global: `${device.name}: ${_format('ai_model_completed_training', {
						default: 'AI model successfully completed training.',
					})}`,
					local: _format('ai_model_completed_training', {
						default: 'AI model successfully completed training.',
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'ai_fail_reason_lower_consumption':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: `${device.name}: ${_format('ai_fail_reason_lower_consumption', {
						default: 'Detected consumption that is lower than usual.',
					})}`,
					local: _format('ai_fail_reason_lower_consumption', {
						default: 'Detected consumption that is lower than usual.',
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'ai_fail_reason_higher_consumption':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: `${device.name}: ${_format('ai_fail_reason_higher_consumption', {
						default: 'Detected consumption that is higher than usual.',
					})}`,
					local: _format('ai_fail_reason_higher_consumption', {
						default: 'Detected consumption that is higher than usual.',
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'ai_fail_reason_longer_sequence':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: `${device.name}: ${_format('ai_fail_reason_longer_sequence', {
						default: 'Detected that your appliance has been turned on longer than usual.',
					})}`,
					local: _format('ai_fail_reason_longer_sequence', {
						default: 'Detected that your appliance has been turned on longer than usual.',
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'ai_fail_reason_shorter_sequence':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: `${device.name}: ${_format('ai_fail_reason_shorter_sequence', {
						default: 'Detected that your appliance has been turned off longer than usual.',
					})}`,
					local: _format('ai_fail_reason_shorter_sequence', {
						default: 'Detected that your appliance has been turned off longer than usual.',
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'bluLowBattery':
		case 'lowBattery':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('notification_global_low_battery', {
						default: `${device.name} has low battery level ${batLevel}%.`,
						values: { deviceName: device.name, batLevel },
					}),
					local: _format('notification_device_drawer_low_battery', {
						default: `Device has low battery level ${batLevel}%.`,
						values: { batLevel },
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'criticalBattery':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: isNonRechargeableBattery(device.type)
						? _format('notification_global_critical_battery_replace', {
								default: `${device.name} has critical battery level ${batLevel}%. Please replace your battery.`,
								values: { deviceName: device.name, batLevel },
							})
						: _format('notification_global_critical_battery', {
								default: `${device.name} has critical battery level ${batLevel}%. Please charge your battery.`,
								values: { deviceName: device.name, batLevel },
							}),
					local: isNonRechargeableBattery(device.type)
						? _format('notification_device_drawer_critical_battery_replace', {
								default: `Device has critical battery level ${batLevel}%. Please replace your battery.`,
								values: { batLevel },
							})
						: _format('notification_device_drawer_critical_battery', {
								default: `Device has critical battery level ${batLevel}%. Please charge your battery.`,
								values: { batLevel },
							}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'batteryError':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('notification_global_battery_error', {
						default: `${device.name} battery error detected.`,
						values: { deviceName: device.name },
					}),
					local: _format('notification_device_drawer_battery_error', {
						default: `Battery error detected.`,
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'cableUnplugged':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('notification_global_cable_unplugged', {
						default: `${device.name}'s cable is unplugged.`,
						values: { deviceName: device.name },
					}),
					local: _format('notification_device_drawer_cable_unplugged', {
						default: `Device has unplugged cable.`,
					}),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('cableUnplugged')
				.build();

		case 'loqedWarning':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('heading_unknown_state', {
						default: `Unknown State`,
					}),
					local: _format('heading_unknown_state', {
						default: `Unknown State`,
					}),
				})
				.setMessage(
					_format('notification_device_drawer_loqed_warning', {
						default: `Device has unknown state. Please check the device.`,
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setDismissable(false)
				.build();

		case 'calibration_error':
			const calibrationCta = createGoToSettingCta(
				device.id,
				'settings',
				'calibration',
				_format(`navigate_to_calibration`, {
					default: 'Navigate to calibration',
				}),
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('notification_global_calibration_error', {
						default: '{deviceName} is not calibrated.',
						values: { deviceName: device.name },
					}),
					local: _format('notification_global_calibration_error', {
						default: '{deviceName} is not calibrated.',
						values: { deviceName: device.name },
					}),
				})
				.setCtaGlobal(calibrationCta)
				.setCtaLocal(calibrationCta)
				.setMessage(
					capFirstChar(
						_format('message_notification_calibration_error', {
							default: 'Device calibration is needed in order to function properly.',
						}),
					),
				)
				.setError('calibration_error')
				.build();

		case 'ext_temp_missing':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('notification_global_ext_temp_missing', {
						default: '{deviceName} No recent temperature data from external sensor.',
						values: { deviceName: device.name },
					}),
					local: _format('notification_global_ext_temp_missing', {
						default: '{deviceName} No recent temperature data from external sensor.',
						values: { deviceName: '' },
					}).trim(),
				})
				.setMessage(
					_format('alert_message_ext_temp_missing', {
						default:
							'No recent temperature data from external sensor. Using last known value.',
					}),
				)
				.setCtaGlobal(
					createGoToSettingCta(
						device.id,
						'sensor_settings',
						'attached_sensors',
						_format(`navigate_to_attached_sensors`, {
							default: 'Go to attached sensors',
						}),
					),
				)
				.setError('ext_temp_missing')
				.build();

		case 'lora_limit_reached':
			const loraLimitCta = createGoToSettingCta(
				device.id,
				'addon_sensors',
				undefined,
				_format(`navigate_to_addon_peripherals`, {
					default: 'Navigate to Add-on peripherals',
				}),
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${_format(`addon_peripheral_limit_reached`, {
						default: 'Safety stop reached',
					})}`,
					local: _format(`addon_peripheral_limit_reached`, {
						default: 'Safety stop reached',
					}),
				})
				.setMessage(
					_format('alert_message_limit_reached_description', {
						default:
							'Description: 25% airtime used in one hour. Transmission will resume when airtime drops below the Recommended Limit.',
					}),
				)
				.setCtaGlobal(loraLimitCta)
				.setCtaLocal(loraLimitCta)
				.setError(`lora_limit_reached`)
				.build();

		case 'lora_send_blocked':
			const loraSendBlockedCta = createGoToSettingCta(
				device.id,
				'addon_sensors',
				undefined,
				_format(`navigate_to_addon_peripherals`, {
					default: 'Navigate to Add-on peripherals',
				}),
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${_format(`addon_peripheral_send_blocked`, {
						default: 'Transmission hold-off active',
					})}`,
					local: _format(`addon_peripheral_send_blocked`, {
						default: 'Transmission hold-off active',
					}),
				})
				.setMessage(
					_format('alert_message_send_blocked_description', {
						default:
							'The device cannot transmit right now due to regulatory limits. It will resume automatically once allowed.',
					}),
				)
				.setCtaGlobal(loraSendBlockedCta)
				.setCtaLocal(loraSendBlockedCta)
				.setError(`lora_send_blocked`)
				.build();

		case 'data_error':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device.name} has ${_format('data_error', {
						default: 'data error',
					})}.`,
					local: '',
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'lora_duty_cycle_limit':
			const loraDutyCycleCta = createGoToSettingCta(
				device.id,
				'addon_sensors',
				undefined,
				_format(`navigate_to_addon_peripherals`, {
					default: 'Navigate to Add-on peripherals',
				}),
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: `${device?.name}: ${_format(`addon_peripheral_duty_cycle_limit`, {
						default: 'Recommended limit reached',
					})}`,
					local: _format(`addon_peripheral_duty_cycle_limit`, {
						default: 'Recommended limit reached',
					}),
				})
				.setMessage(
					_format('alert_message_duty_cycle_limit_description', {
						default: "The selected subband's Recommended Limit has been reached.",
					}),
				)
				.setError(`lora_duty_cycle_limit`)
				.setCtaGlobal(loraDutyCycleCta)
				.setCtaLocal(loraDutyCycleCta)
				.build();

		case 'overtemp':
			const overtempCta = createGoToSafetyCta(device.id);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${capFirstChar(
						_format(`overtemp_detected`, {
							default: `Overtemperature detected`,
						}),
					)}`,
					local: `${capFirstChar(
						_format(`overtemp_detected`, {
							default: `Overtemperature detected`,
						}),
					)}`,
				})
				.setMessage(
					`${_format('error_over_temp_alert', {
						default:
							'Exceeded the configured temperature limit. The device has been turned off to prevent damage.`',
					})}`,
				)
				.setCtaGlobal(overtempCta)
				.setCtaLocal(overtempCta)
				.build();

		case 'overvoltage':
			const overvoltageCta = createGoToSafetyCta(
				device.id,
				DIMMERS_WITH_CALIBRATION.includes(device.type)
					? 'over_voltage_protection'
					: 'max_voltage_protection',
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${capFirstChar(
						_format('overvoltage_detected', {
							default: 'Overvoltage detected',
						}),
					)}`,
					local: capFirstChar(
						_format('overvoltage_detected', {
							default: 'Overvoltage detected',
						}),
					),
				})
				.setMessage(
					_format('error_over_voltage_alert', {
						default: `Exceeded the configured voltage limit. The device has been turned off to prevent damage.`,
					}),
				)
				.setError('overvoltage')
				.setCtaGlobal(overvoltageCta)
				.setCtaLocal(overvoltageCta)
				.build();

		case 'overcurrent':
			const overcurrentCta = createGoToSafetyCta(device.id, 'max_current_protection');
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${capFirstChar(
						_format('overcurrent_detected', {
							default: 'Overcurrent detected',
						}),
					)}`,
					local: capFirstChar(
						_format('overcurrent_detected', {
							default: 'Overcurrent detected',
						}),
					),
				})
				.setMessage(
					_format('error_over_current_alert', {
						default: `Exceeded the configured current limit. The device has been turned off to prevent damage.`,
					}),
				)
				.setCtaGlobal(overcurrentCta)
				.setCtaLocal(overcurrentCta)
				.setError('overcurrent')
				.build();

		case 'undervoltage':
			const undervoltageCta = createGoToSafetyCta(
				device.id,
				DIMMERS_WITH_CALIBRATION.includes(device.type)
					? 'under_voltage_protection'
					: 'max_voltage_protection',
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${capFirstChar(
						_format('undervoltage_detected', {
							default: 'Undervoltage detected',
						}),
					)}`,
					local: capFirstChar(
						_format('undervoltage_detected', {
							default: 'Undervoltage detected',
						}),
					),
				})
				.setMessage(
					_format('error_under_voltage_alert', {
						default: `Dropped below the configured voltage limit. The device has been turned off to prevent damage.`,
					}),
				)
				.setCtaGlobal(undervoltageCta)
				.setCtaLocal(undervoltageCta)
				.setError('undervoltage')
				.build();

		case 'obstruction':
			const obstructionCta = createGoToSafetyCta(device.id, 'obstacle_detection');
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${_format('error_obstruction_heading', {
						default: `Obstruction detected`,
					})}`,
					local: _format('error_obstruction_heading', {
						default: `Obstruction detected`,
					}),
				})
				.setMessage(
					_format('error_obstruction', {
						default: `Obstruction was hit, cover will execute a configured recovery action (stop or reverse).`,
					}),
				)
				.setCtaGlobal(obstructionCta)
				.setCtaLocal(obstructionCta)
				.build();

		case 'safety_switch':
			const safetySwitchCta = createGoToSafetyCta(device.id, 'safety_switch');
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${_format('error_safety_switch_heading', {
						default: `Safety switch triggered`,
					})}`,
					local: _format('error_safety_switch_heading', {
						default: `Safety switch triggered`,
					}),
				})
				.setMessage(
					_format('error_safety_switch', {
						default: `Safety switch triggered. Cover will execute a configured recovery action (stop, reverse, or pause).`,
					}),
				)
				.setCtaGlobal(safetySwitchCta)
				.setCtaLocal(safetySwitchCta)
				.build();

		case 'bad_feedback:rotating_in_wrong_direction':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${_format('error_motor_rotation_heading', {
						default: `Motor rotation error`,
					})}`,
					local: _format('error_motor_rotation_heading', {
						default: `Motor rotation error`,
					}),
				})
				.setMessage(
					_format('bad_feedback_rotating_in_wrong_direction', {
						default: `Device reported that the motor is rotating in a different direction than requested. As a precaution, the device turned off both outputs.`,
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'bad_feedback:both_directions_active':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${_format('error_output_conflict_heading', {
						default: `Output conflict detected`,
					})}`,
					local: _format('error_output_conflict_heading', {
						default: `Output conflict detected`,
					}),
				})
				.setMessage(
					_format('bad_feedback_both_directions_active', {
						default: `Device reported both outputs are connected and active, which should never happen. As a precaution, the device turned off both outputs.`,
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'bad_feedback:failed_to_halt':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: `${device?.name}: ${_format('error_failed_to_halt_heading', {
						default: `Failed to stop`,
					})}`,
					local: _format('error_failed_to_halt_heading', {
						default: `Failed to stop`,
					}),
				})
				.setMessage(
					_format('bad_feedback_failed_to_halt', {
						default: `Device reported it is still moving though it was supposed to stop. As a precaution, the device turned off both outputs.`,
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();

		case 'cal_abort:timeout_open':
		case 'cal_abort:timeout_close':
		case 'cal_abort:implausible_time_to_fully_open':
		case 'cal_abort:implausible_time_to_fully_close':
		case 'cal_abort:implausible_time_to_fully_open_w_steps':
		case 'cal_abort:implausible_time_to_fully_close_w_steps':
			const movementTimeLimitCta = createGoToSettingCta(
				device.id,
				'settings',
				'movement_time_limits',
				`${capFirstChar(_format('button_update'))} "${capFirstChar(
					_format('sub_settings_title_movement_time_limits'),
				)}"`,
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format(notification.replace(':', '_'), {
						default: `Calibration failed due to movement time limit exceeded.`,
					}),
					local: _format(notification.replace(':', '_'), {
						default: `Calibration failed due to movement time limit exceeded.`,
					}),
				})
				.setCtaGlobal(movementTimeLimitCta)
				.setCtaLocal(movementTimeLimitCta)
				.setError('calibration_error')
				.build();

		case 'cal_abort:safety':
		case 'cal_abort:ext_command':
		case 'cal_abort:bad_feedback':
		case 'cal_abort:implausible_power_consumption_in_close_dir':
		case 'cal_abort:implausible_power_consumption_in_open_dir':
		case 'cal_abort:implausible_step_duration_in_close_dir':
		case 'cal_abort:too_many_steps_to_close':
		case 'cal_abort:too_few_steps_to_close':
		case 'cal_abort:implausible_step_duration_in_open_dir':
		case 'cal_abort:too_many_steps_to_open':
		case 'cal_abort:too_few_steps_to_open':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setMessage(
					_format(notification.replace(':', '_'), {
						default: `Calibration failed due to ${notification
							.replace('cal_abort:', '')
							.replace(/_/g, ' ')}.`,
						values: {
							commonCoverSuggestionKey: _format('cal_abort_common_suggestion', {
								default:
									'Calibration failed, because of invalid state was reached. Please verify the connected hardware and wiring.',
							}),
						},
					}),
				)
				.setHeading({
					global: `${device?.name}: ${capFirstChar(
						_format('cal_error_detected', {
							default: 'Calibration problem detected',
						}),
					)}`,
					local: capFirstChar(
						_format('cal_error_detected', {
							default: 'Calibration problem detected',
						}),
					),
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('calibration_error')
				.build();

		case 'cal_abort:interrupted':
		case 'cal_abort:power_read':
		case 'cal_abort:no_load':
		case 'cal_abort:non_dimmable':
		case 'cal_abort:overpower':
		case 'cal_abort:unsupported_load':
		case 'cal_abort:no_synchro':
			const calibrateButtonCta = createGoToSettingCta(
				device.id,
				'settings',
				'calibration',
				capFirstChar(_format('button_calibrate', { default: 'Calibrate' })),
			);
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setMessage(
					_format(notification.replace(':', '_'), {
						default: `Calibration error: ${notification.replace('cal_abort:', '').replace(/_/g, ' ')}.`,
					}),
				)
				.setDismissable(false)
				.setHeading({
					global: `${device?.name}: ${capFirstChar(
						_format('cal_error_detected', {
							default: 'Calibration problem detected',
						}),
					)}`,
					local: capFirstChar(
						_format('cal_error_detected', {
							default: 'Calibration problem detected',
						}),
					),
				})
				.setCtaGlobal(calibrateButtonCta)
				.setCtaLocal(calibrateButtonCta)
				.setError('calibration_error')
				.build();

		case 'low_temperature':
		case 'high_temperature':
		case 'faulty_temperature_sensor':
		case 'low_pressure':
		case 'high_pressure':
		case 'faulty_pressure_sensor':
		case 'low_flow_rate':
		case 'high_flow_rate':
		case 'faulty_flow_rate_sensor':
		// Ev Charger
		case 'overcurrent_fault':
		case 'contactor_adhesion':
		case 'contactor_fault':
		case 'earth_fault':
		case 'meter_hardware':
		case 'control_pilot':
		case 'meter_communication':
		case 'temperature_sensor':
		case 'relay_stick':
		case 'current_leakage':
		case 'self_test_leakage':
		case 'auto_balance_timeout':
		case 'auto_balance_em_not_found':
			const isCriticalError = (status?.['service:0']?.errors || []).includes(notification);
			const notificationError = capFirstChar(
				_format(`notification_error_${notification}`, {
					default: notification.replace(/_/g, ' '),
				}),
			);
			return new DeviceNotificationBuilder(
				`${device.id}-${notification}`,
				isCriticalError ? 'danger' : 'warning',
			)
				.setHeading({
					global: `${device?.name}: ${notificationError}`,
					local: notificationError,
				})
				.setDismissable(!isCriticalError)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setCtaLocal({
					title: '',
				})
				.setError(`${notification}`)
				.build();

		// Vial fault notifications for Cury devices
		case 'vial_fault_left_non_genuine':
		case 'vial_fault_right_non_genuine':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('notification_global_non_genuine_vial_fault', {
						default: '{deviceName} - Non-genuine vial detected',
						values: { deviceName: device.name },
					}),
					local: _format('notification_local_non_genuine_vial_fault', {
						default: 'Non-genuine vial detected',
					}),
				})
				.setMessage(
					_format('message_notification_non_genuine_vial', {
						default: 'Please use genuine vials for optimal performance and safety.',
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('vial_fault_non_genuine')
				.build();

		case 'vial_fault_left_tag_error':
		case 'vial_fault_right_tag_error':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: _format('notification_global_nfc_vial_fault', {
						default: '{deviceName} - NFC tag problem',
						values: { deviceName: device.name },
					}),
					local: _format('notification_local_nfc_vial_fault', {
						default: 'NFC tag communication problem',
					}),
				})
				.setMessage(
					_format('message_notification_tag_error', {
						default: "There is a problem with the vial's NFC tag communication.",
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('vial_fault_tag_error')
				.build();

		case 'vial_fault_left_expired':
		case 'vial_fault_right_expired':
			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'danger')
				.setHeading({
					global: _format('notification_global_expired_vial_fault', {
						default: '{deviceName} - Vial expired',
						values: { deviceName: device.name },
					}),
					local: _format('notification_local_expired_vial_fault', {
						default: 'Vial liquid is expired',
					}),
				})
				.setMessage(
					_format('message_notification_expired_vial', {
						default: 'The vial liquid has expired. Please replace with a new vial.',
					}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('vial_fault_expired')
				.build();

		case 'vial_fault_left_empty':
		case 'vial_fault_right_empty': {
			const parsedFault = parseVialFaultNotification(notification);
			const channelStructure = get_gen_2_component_channel_structure(device);
			const vialName = parsedFault
				? getSlotVialName(status?.[channelStructure]?.slots, parsedFault.slot)
				: undefined;

			const emptyVialBuilder = new DeviceNotificationBuilder(
				`${device.id}-${notification}`,
				'warning',
			)
				.setHeading({
					global: vialName
						? _format('notification_global_empty_vial_fault_named', {
								default: '{deviceName} — {vialName} is empty',
								values: { deviceName: device.name, vialName },
							})
						: _format('notification_global_empty_vial_fault', {
								default: '{deviceName} - Vial empty',
								values: { deviceName: device.name },
							}),
					local: formatEmptyVialMessage(
						vialName,
						_format,
						'notification_local_empty_vial_fault_named',
						'{vialName} is empty',
						'notification_local_empty_vial_fault',
						'No liquid in vial',
					),
				})
				.setMessage(
					vialName
						? _format('message_notification_empty_vial_named_suffix', {
								default: 'is empty. Please replace with a new vial.',
							})
						: _format('message_notification_empty_vial', {
								default: 'The vial is empty. Please replace with a new vial.',
							}),
				)
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.setError('vial_fault_empty');

			if (vialName) {
				emptyVialBuilder.setMessageHighlight(vialName);
			}

			return emptyVialBuilder.build();
		}

		default:
			const globalHeading = _format('notification_error_triggered', {
				default: `${device.name} has encountered an error: "${notification}".`,
				values: {
					deviceName: device.name,
					error: _format(notification),
				},
			});

			return new DeviceNotificationBuilder(`${device.id}-${notification}`, 'warning')
				.setHeading({
					global: globalHeading,
					local: globalHeading,
				})
				.setCtaGlobal(createGoToDeviceCta(device.id))
				.build();
	}
};

/**
 * Helper to check the conditions for overpower notification
 * @param status - The device status object
 * @param settings - The device settings object
 * @param deviceChannel - The channel number to check
 * @returns True if overpower condition is met, false otherwise
 */
export const checkOverpowerCondition = (status: any, settings: any, deviceChannel: number): boolean => {
	if (typeof status === 'undefined' || typeof settings === 'undefined') {
		return false;
	}

	if (status?.meters?.[deviceChannel]?.overpower > status.meters?.[deviceChannel]?.power) {
		return true;
	}

	if (
		settings?.emeters?.[deviceChannel]?.max_power > 0 &&
		settings?.emeters?.[deviceChannel]?.max_power < status?.emeters?.[deviceChannel]?.power
	)
		return true;

	return false;
};

/**
 * Put the notification in the dismissed store, assigning it a timestamp
 * @param id Combination of device id and notification type, format 'asd656srd65ad-overpower'
 */
export const dismissNotification = (id: string): void => {
	dismissed_notifications.dismiss(id);

	if (id?.split('-')[1] === 'ai_is_completed') {
		ai_model_ready.update(id?.split('-')[0], {
			key: 'model_ready',
			value: false,
		});
	} else if (id?.split('-')[1] === 'fail_detection') {
		ai_model_ready.update(id?.split('-')[0], {
			key: 'fail_detection',
			value: false,
		});
	}
};

/**
 * Translates vial fault error codes into localized error messages and short labels.
 *
 * This function maps vial fault error codes to their corresponding translated error messages
 * and short labels using the application's internationalization system. It supports various
 * vial fault types including non-genuine vials, NFC tag errors, expired liquids, and empty vials.
 *
 * @param fault - The fault code to translate. Supported values:
 *   - 'non_genuine': Non-genuine or counterfeit vial detected
 *   - 'tag_error': NFC tag communication problem
 *   - 'expired': Vial liquid has expired
 *   - 'empty': No liquid remaining in vial
 *
 * @returns An object containing translated error information or undefined if fault code is not recognized
 *
 * @example
 * // Translate a non-genuine vial fault
 * const errorInfo = translateVialFaultError('non_genuine');
 * console.log(errorInfo?.short); // "Non-genuine"
 * console.log(errorInfo?.error); // "Non-genuine vial"
 *
 * @example
 * // Translate an expired vial fault
 * const errorInfo = translateVialFaultError('expired');
 * console.log(errorInfo?.short); // "Expired"
 * console.log(errorInfo?.error); // "Vial liquid is expired"
 *
 */
export const translateVialFaultError = (fault: string): VialFaultErrorInfo | undefined => {
	const $format = get(format);
	const vialFaultErrors: Record<VialFaultType, VialFaultErrorInfo> = {
		non_genuine: {
			short: $format('label_short_non_genuine', { default: 'Non-genuine' }),
			error: $format('label_non_genuine_vial', {
				default: 'Non-genuine vial',
			}),
		},
		tag_error: {
			short: $format('label_short_tag_error', { default: 'NFC tag problem' }),
			error: $format('label_tag_error', {
				default: 'NFC tag communication problem',
			}),
		},

		expired: {
			short: $format('label_short_expired', { default: 'Expired' }),
			error: $format('label_vial_expired', {
				default: 'Vial liquid is expired',
			}),
		},

		empty: {
			short: $format('label_short_empty', { default: 'Empty' }),
			error: $format('label_vial_empty', { default: 'No liquid in vial' }),
		},
	};
	return vialFaultErrors[fault as VialFaultType];
};

/**
 * Extracts vial fault errors from a collection of vials data.
 *
 * This function processes a collection of vials and extracts only those that have
 * vial fault errors, returning a filtered object containing only vials with faults.
 * It safely handles invalid input and filters out vials without fault information.
 *
 * @param vials - An object containing vial data where each key represents a vial identifier
 *   and the value contains vial information including potential fault data in the format:
 *   `{ vial: { vial_fault: string } }`
 *
 * @returns An object containing only vials with fault errors (keys: vial IDs, values: fault codes),
 *   or an empty object if input is invalid or no vials have faults
 *
 * @example
 * // Extract faults from vials with mixed fault states
 * const vials = {
 *   'vial1': { vial: { vial_fault: 'non_genuine' } },
 *   'vial2': { vial: { vial_fault: 'expired' } },
 *   'vial3': { vial: {} }, // No fault
 *   'vial4': { vial: { vial_fault: 'empty' } }
 * };
 * const faults = getVialFaultError(vials);
 * // Result: { 'vial1': 'non_genuine', 'vial2': 'expired', 'vial4': 'empty' }
 *
 * @example
 * // Handle invalid input
 * const faults = getVialFaultError(null);
 * // Result: {}
 *
 */
export const getVialFaultError = (vials: Record<string, any>): Record<string, string> => {
	//status?.[component]?.slots?.left?.vial?.vial_fault
	if (!vials || typeof vials !== 'object') return {};

	return Object.fromEntries(
		Object.entries(vials)
			.filter(([, vial]) => vial?.vial?.vial_fault)
			.map(([key, vial]) => [key, vial.vial.vial_fault]),
	);
};
